import { useEffect, useRef, useState } from "react";
import { logger } from "../lib/logger";
import type { LogEntry } from "../lib/logger";

interface LogsPanelProps {
  visible: boolean;
  onClose: () => void;
}

// Couleurs par source
const SRC_COLORS: Record<string, string> = {
  webrtc: "#3B82F6",
  gladia: "#22C55E",
  "gladia-ws": "#22C55E",
  "audio-mixer": "#22C55E",
  api: "#E5960A",
  session: "#8B5CF6",
  datachannel: "#EC4899",
  mcp: "#6C3AED",
  console: "#EF4444",
  window: "#EF4444",
  promise: "#EF4444",
  click: "#94A3B8",
};

function srcColor(src: string): string {
  return SRC_COLORS[src] ?? "#9CA3AF";
}

// Mapping level filter → types inclus
type LevelFilter = "all" | "info+" | "warn+" | "error";
const LEVEL_INCLUDES: Record<LevelFilter, string[]> = {
  "all": ["api", "nav", "action", "debug", "info", "warn", "error"],
  "info+": ["info", "warn", "error", "api", "action"],
  "warn+": ["warn", "error"],
  "error": ["error"],
};

export default function LogsPanel({ visible, onClose }: LogsPanelProps) {
  const [entries, setEntries] = useState<LogEntry[]>(() => logger.getBuffer());
  const [levelFilter, setLevelFilter] = useState<LevelFilter>("all");
  const [srcFilter, setSrcFilter] = useState<string>("all");
  const bottomRef = useRef<HTMLDivElement>(null);

  // Subscribe to live updates
  useEffect(() => {
    if (!visible) return;
    setEntries(logger.getBuffer()); // sync on open
    const unsub = logger.subscribe((all) => {
      setEntries([...all]);
    });
    return unsub;
  }, [visible]);

  // Auto-scroll to bottom on new entries
  useEffect(() => {
    if (visible) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [entries, visible]);

  if (!visible) return null;

  // Filter
  const allowed = LEVEL_INCLUDES[levelFilter];
  const filtered = entries
    .filter((e) => allowed.includes(e.type))
    .filter((e) => srcFilter === "all" || e.src === srcFilter)
    .slice(-200); // max 200 lignes affichées

  // Sources uniques pour le dropdown
  const allSrcs = Array.from(new Set(entries.map((e) => e.src))).sort();

  return (
    <div
      style={{
        borderTop: "1px solid #333",
        background: "#0a0a0a",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        height: 200,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 8px",
          borderBottom: "1px solid #222",
          background: "#111",
        }}
      >
        <span style={{ color: "#9CA3AF", fontSize: 11, fontFamily: "monospace" }}>
          LOGS ({filtered.length})
        </span>
        {/* Level filter */}
        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value as LevelFilter)}
          style={{ fontSize: 11, background: "#1f2937", color: "#d1d5db", border: "1px solid #374151", borderRadius: 3 }}
        >
          <option value="all">All</option>
          <option value="info+">Info+</option>
          <option value="warn+">Warn+</option>
          <option value="error">Error</option>
        </select>
        {/* Source filter */}
        <select
          value={srcFilter}
          onChange={(e) => setSrcFilter(e.target.value)}
          style={{ fontSize: 11, background: "#1f2937", color: "#d1d5db", border: "1px solid #374151", borderRadius: 3 }}
        >
          <option value="all">All sources</option>
          {allSrcs.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <button
          onClick={onClose}
          style={{ fontSize: 11, background: "none", color: "#6b7280", border: "none", cursor: "pointer" }}
        >
          ✕
        </button>
      </div>
      {/* Log rows */}
      <div style={{ flex: 1, overflowY: "auto", fontFamily: "monospace", fontSize: 11 }}>
        {filtered.map((entry, i) => (
          <LogRow key={i} entry={entry} />
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

function LogRow({ entry }: { entry: LogEntry }) {
  const color = srcColor(entry.src);
  const typeColor =
    entry.type === "error" ? "#EF4444" :
    entry.type === "warn"  ? "#F59E0B" :
    entry.type === "api"   ? "#E5960A" :
    entry.type === "debug" ? "#6b7280" : "#d1d5db";

  // Build message
  let msg = entry.msg ?? entry.action ?? "";
  if (entry.type === "api") {
    msg = `${entry.method} ${entry.endpoint} ${entry.status} ${entry.ms}ms`;
  }
  const dataStr = entry.data ? " " + JSON.stringify(entry.data).slice(0, 120) : "";

  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        padding: "1px 8px",
        borderBottom: "1px solid #111",
        whiteSpace: "nowrap",
        overflow: "hidden",
        color: typeColor,
      }}
    >
      <span style={{ color: "#4b5563", flexShrink: 0 }}>{entry.ts}</span>
      <span style={{ color, flexShrink: 0, minWidth: 80 }}>[{entry.src}]</span>
      <span style={{ color: "#6b7280", flexShrink: 0, minWidth: 52 }}>[{entry.type}]</span>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
        {msg}{dataStr}
      </span>
    </div>
  );
}
